import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableBody, TableCell, TableRow } from "../../../../../components/table";
import { Badge } from "../../../../../components/badge";
import { Button } from "../../../../../components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/tooltip";
import { Image as ImageIcon, Edit, Trash2, QrCode } from "lucide-react";
import { getDemoEventCampaignMap } from "../../../../campaign/api/campaign.api";
import { formatEventDate, formatEventTime } from "../../utils/event.utils";

interface EventContentProps {
  events: any[];
  onRowOpen: (eventItem: any) => void;
  onEdit: (eventItem: any) => void;
  onDelete: (eventItem: any) => void;
  onQRCode?: (eventItem: any) => void;
  getEventStatus: (eventItem: any) => string;
}

export const EventContent: React.FC<EventContentProps> = ({
  events,
  onRowOpen,
  onEdit,
  onDelete,
  onQRCode,
  getEventStatus,
}) => {
  const { t } = useTranslation();
  const demoEventCampaignMap = useMemo(() => getDemoEventCampaignMap(), []);

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case "upcoming":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "ongoing":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "completed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getDemoCampaignInfo = (eventItem: any) => {
    const apiCampaignName = eventItem.campaign?.name?.trim();
    const apiProgramName = eventItem.campaign?.programName?.trim();
    const mappedItem = demoEventCampaignMap[eventItem.id];

    if (!mappedItem && !apiCampaignName) {
      return null;
    }

    return {
      campaignName: apiCampaignName || mappedItem?.campaignName || "N/A",
      programName: apiProgramName || mappedItem?.programName || "N/A",
    };
  };

  return (
    <TooltipProvider delayDuration={150}>
      <TableBody>
        {events.map((eventItem: any) => {
          const campaignInfo = getDemoCampaignInfo(eventItem);

          return (
            <TableRow
              key={eventItem.id}
              style={{ height: "75px" }}
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => onRowOpen(eventItem)}
            >
              <TableCell className="w-16 py-0">
                {eventItem.image_url ? (
                  <img
                    src={eventItem.image_url}
                    alt={eventItem.title}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </TableCell>
              <TableCell className="w-32 py-0">
                <div className="max-w-[160px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="truncate"
                        title={eventItem.title}
                      >
                        {eventItem.title}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="start"
                      className="max-w-[280px] rounded-xl border border-[#DDEBE3] bg-white px-4 py-3 text-left shadow-lg"
                    >
                      <div className="space-y-2">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
                            Event name
                          </div>
                          <div className="mt-1 text-sm font-semibold text-[#0F172A]">
                            {eventItem.title}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
                            Program name
                          </div>
                          <div className="mt-1 text-sm text-[#0F172A]">
                            {campaignInfo?.programName || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
                            Campaign name
                          </div>
                          <div className="mt-1 text-sm text-[#0F172A]">
                            {campaignInfo?.campaignName || "N/A"}
                          </div>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  {campaignInfo ? (
                    <div className="mt-1 flex flex-col gap-1">
                      <span className="inline-flex w-fit rounded-full bg-[#F3FBF6] px-2 py-0.5 text-[10px] font-semibold text-[#004232]">
                        {campaignInfo.campaignName}
                      </span>
                      <span className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                        {campaignInfo.programName}
                      </span>
                    </div>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="w-40 py-0">
                <div
                  className="max-h-[90px] w-[120px] overflow-hidden text-center"
                  title={[
                    ...(eventItem.categories?.map((c: any) => c.name) || []),
                    ...(eventItem.interests?.map((i: any) => i.name) || []),
                  ].join(", ")}
                >
                  <div className="flex flex-col items-center justify-center gap-1">
                    {eventItem.categories && eventItem.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {eventItem.categories.map((category: any) => (
                          <span
                            key={category.id}
                            className="font-bold text-xs text-gray-900 dark:text-gray-400 leading-tight"
                          >
                            {category.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {eventItem.interests && eventItem.interests.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {eventItem.interests.map((interest: any) => (
                          <span
                            key={interest.id}
                            className="text-xs text-gray-500 dark:text-gray-200 leading-tight"
                          >
                            {interest.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-xs py-0">
                {formatEventDate(eventItem.event_date, eventItem.time_zone)}
              </TableCell>
              <TableCell className="w-32 text-xs py-0">
                <div
                  className="truncate max-w-[150px]"
                  title={eventItem.location || `${eventItem.latitude}, ${eventItem.longitude}`}
                >
                  {eventItem.location || `${eventItem.latitude}, ${eventItem.longitude}`}
                </div>
              </TableCell>
              <TableCell className="w-28 py-0">
                <div className="flex items-center justify-center">
                  <span className="text-xs whitespace-nowrap">
                    {(() => {
                      const startTime = formatEventTime(eventItem.start_time, eventItem.time_zone);
                      const endTime = formatEventTime(eventItem.end_time, eventItem.time_zone);
                      return `${startTime}-${endTime}`;
                    })()}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-0">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-medium">{eventItem.points || 0}</span>
                </div>
              </TableCell>
              <TableCell className="py-0">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-medium">
                    {eventItem.statistics?.upcoming_count ||
                      eventItem.statistics?.ongoing_count ||
                      0}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-0">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-medium">
                    {eventItem.statistics?.completed_count || 0}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-0">
                <Badge className={getEventStatusColor(getEventStatus(eventItem))}>
                  {(() => {
                    const status = getEventStatus(eventItem);
                    return status === "upcoming"
                      ? t("event.upcoming")
                      : status === "ongoing"
                        ? t("event.ongoing")
                        : t("event.completed");
                  })()}
                </Badge>
              </TableCell>
              <TableCell className="py-0">
                <div className="flex gap-2">
                  {onQRCode && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-8 h-8 p-0 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQRCode(eventItem);
                      }}
                    >
                      <QrCode className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-8 h-8 p-0 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(eventItem);
                    }}
                  >
                    <Edit className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-8 h-8 p-0 bg-white border-gray-300 hover:bg-gray-50 text-red-600 hover:text-red-700 rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-red-600 dark:hover:text-red-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(eventItem);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </TooltipProvider>
  );
};

export default EventContent;
